/**
 * Keyboard guard utility for canvas editors.
 *
 * Prevents canvas keyboard shortcuts (Cmd+Z, Cmd+C, Cmd+V, Cmd+F, Cmd+A)
 * from intercepting events when the user is interacting with a dialog,
 * popover, or input field.
 */

/**
 * Check if a keyboard event should be ignored by canvas shortcuts.
 *
 * Returns true if the event target is inside:
 * - A MUI Dialog (role="dialog")
 * - A MUI Modal/Popover (role="presentation" with MuiModal-root)
 * - An input, textarea, select, or contentEditable element
 *
 * Canvas shortcuts should call this at the top and return early if true,
 * allowing the dialog or input to handle the event natively.
 */
export function isKeyboardCapturedByOverlay(event: KeyboardEvent): boolean {
  const target = event.target as HTMLElement | null;
  if (!target) return false;

  // Check if target is inside a dialog or modal overlay
  if (target.closest('[role="dialog"], [role="presentation"].MuiModal-root, .MuiPopover-root')) {
    return true;
  }

  // Check if target is an interactive input element
  const tagName = target.tagName.toLowerCase();
  if (tagName === 'input' || tagName === 'textarea' || tagName === 'select') {
    return true;
  }

  // Check for contentEditable elements (code editors, rich text)
  if (target.isContentEditable) {
    return true;
  }

  return false;
}
